import { createPortal } from 'react-dom';
import { FolderPlus, Plus, Search, X, Upload, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FileNode } from '@/lib/fileUtils';
import FileBreadcrumb from './FileBreadcrumb';
import FileSelectionToolbar from './FileSelectionToolbar';
import EmptyFilesDropZone from './EmptyFilesDropZone';
import DragOverlay from './DragOverlay';
import FileListItem from './FileListItem';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface FilesSectionProps {
  // Project info
  projectId: string;
  isViewer?: boolean; // 🔥 是否为 viewer 角色（只读模式）

  // File state
  currentNodes: FileNode[];
  currentPath: FileNode[];
  isLoadingFiles: boolean;
  fileSearchQuery: string;
  setFileSearchQuery: (query: string) => void;

  // Selection state
  selectedFileIds: Set<string>;
  hoveredFileId: string | null;
  setHoveredFileId: (id: string | null) => void;
  allFilesSelected: boolean;
  totalSelectableFiles: number;

  // Selection actions
  toggleFileSelect: (fileId: string, event: React.MouseEvent) => void;
  clearFileSelection: () => void;
  handleSelectAllFiles: () => void;

  // Upload state
  isUploadingFiles: boolean;
  isUploadingToFolder: boolean;
  isDraggingOver: boolean;
  isCreatingFolder: boolean;
  isMaxDepth: boolean;

  // Upload actions
  handleDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: React.DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => Promise<void>;
  triggerFileUpload: () => void;
  onCreateFolder: () => void;
  onAddTextContent: () => void;

  // File operations
  handleFolderClick: (folder: FileNode) => void;
  handleBreadcrumbClick: (index: number) => void;
  handleOpenMoveModal: () => void;
  handleBatchDelete: () => Promise<void>;
  handleBatchDownload: () => Promise<void>;
  handleDownloadFile: (node: FileNode, e: React.MouseEvent) => void;
  handleDeleteFile: (node: FileNode, e: React.MouseEvent) => void;
  handleOpenEditFolder: (node: FileNode, e: React.MouseEvent) => void;
  openFilePreview: (node: FileNode, fileUrl: string) => void;

  // Box selection
  filesContainerRef: React.RefObject<HTMLDivElement>;
  fileItemRefs: React.MutableRefObject<Map<string, HTMLElement>>;
  isSelectingFiles: boolean;
  filesSelectionBoxStyle: React.CSSProperties;
  handleFilesMouseDown: (e: React.MouseEvent) => void;
}

export default function FilesSection({
  // Project info
  projectId,
  isViewer = false,

  // File state
  currentNodes,
  currentPath,
  isLoadingFiles,
  fileSearchQuery,
  setFileSearchQuery,

  // Selection state
  selectedFileIds,
  hoveredFileId,
  setHoveredFileId,

  allFilesSelected,
  totalSelectableFiles,

  // Selection actions
  toggleFileSelect,
  clearFileSelection,
  handleSelectAllFiles,

  // Upload state
  isUploadingFiles,
  isUploadingToFolder,
  isDraggingOver,
  isCreatingFolder,
  isMaxDepth,

  // Upload actions
  handleDragOver,
  handleDragLeave,
  handleDrop,
  triggerFileUpload,
  onCreateFolder,
  onAddTextContent,

  // File operations
  handleFolderClick,
  handleBreadcrumbClick,
  handleOpenMoveModal,
  handleBatchDelete,
  handleBatchDownload,
  handleDownloadFile,
  handleDeleteFile,
  handleOpenEditFolder,
  openFilePreview,

  // Box selection
  filesContainerRef,
  fileItemRefs,
  isSelectingFiles,
  filesSelectionBoxStyle,
  handleFilesMouseDown,
}: FilesSectionProps) {
  const { t } = useTranslation();

  return (
    <div
      className="flex-1 min-h-0"
      onDragOver={isViewer ? undefined : handleDragOver}
      onDragEnter={isViewer ? undefined : handleDragOver}
      onDragLeave={isViewer ? undefined : handleDragLeave}
      onDrop={isViewer ? undefined : handleDrop}
    >
      <div className="h-auto min-h-0 lg:min-h-[18rem] 2xl:min-h-[20rem] max-h-full overflow-visible flex flex-col relative">
        {/* First row: Title, search, folder/upload buttons */}
        <div className="flex items-center justify-between mb-2 gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <h3 className="text-base font-medium text-gray-900 flex-shrink-0">
              {t('project.files')}
            </h3>
            {/* Search input */}
            <div className="relative flex-1 max-w-[240px] ml-2">
              <input
                type="text"
                value={fileSearchQuery}
                onChange={e => setFileSearchQuery(e.target.value)}
                placeholder={t('project.searchFiles')}
                className="w-full pl-8 pr-8 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-600 focus:border-transparent"
              />
              <Search
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
              />
              {fileSearchQuery && (
                <button
                  onClick={() => setFileSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* 🔥 viewer 角色禁用 Create Folder */}
            <button
              onClick={onCreateFolder}
              disabled={
                isUploadingFiles || isCreatingFolder || isMaxDepth || isViewer
              }
              className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                isViewer
                  ? t('project.viewersCannotCreateFolders')
                  : isMaxDepth
                    ? t('project.maxFolderDepthReached')
                    : t('project.createFolder')
              }
            >
              <FolderPlus size={18} />
            </button>
            {/* 🔥 viewer 角色禁用 Add files */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  disabled={isUploadingFiles || isViewer}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-100 disabled:opacity-50"
                  title={
                    isViewer
                      ? t('project.viewersCannotUploadFiles')
                      : t('project.addFiles')
                  }
                >
                  {isUploadingFiles ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b border-gray-400"></div>
                  ) : (
                    <Plus size={18} />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem
                  onClick={triggerFileUpload}
                  disabled={isViewer}
                  className={`flex items-center gap-2 ${isViewer ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <Upload size={16} />
                  <span>{t('project.uploadFromDevice')}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={onAddTextContent}
                  disabled={isViewer}
                  className={`flex items-center gap-2 ${isViewer ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                >
                  <FileText size={16} />
                  <span>{t('project.addTextContent')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Second row: Breadcrumb on left, All button + selected count on right */}
        <div className="flex items-center justify-between mb-2 py-1.5 min-h-[36px]">
          {/* Left side: Breadcrumb only */}
          <FileBreadcrumb
            currentPath={currentPath}
            onBreadcrumbClick={handleBreadcrumbClick}
          />

          {/* Right side: All button with checkbox */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* All button with checkbox */}
            {totalSelectableFiles > 0 && (
              <button
                onClick={() => {
                  if (allFilesSelected) {
                    clearFileSelection();
                  } else {
                    handleSelectAllFiles();
                  }
                }}
                className="flex items-center gap-1.5 text-sm px-2 py-0.5 rounded transition-colors text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              >
                <div
                  className={`w-4 h-4 border rounded flex items-center justify-center ${
                    allFilesSelected
                      ? 'bg-[#2c84db] border-[#2c84db]'
                      : selectedFileIds.size > 0
                        ? 'bg-[#2c84db] border-[#2c84db]'
                        : 'border-gray-400 bg-white'
                  }`}
                >
                  {allFilesSelected ? (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path d="M5 13l4 4L19 7" />
                    </svg>
                  ) : selectedFileIds.size > 0 ? (
                    <svg
                      className="w-2.5 h-2.5 text-white"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M2 12H20"
                        strokeWidth="2.5"
                        stroke="currentColor"
                        strokeLinecap="round"
                      ></path>
                    </svg>
                  ) : null}
                </div>
                {t('common.all')}
              </button>
            )}
          </div>
        </div>

        {/* Operation buttons toolbar (shown when files selected) */}
        {/* 🔥 传递 isViewer 以禁用 viewer 的批量操作 */}
        <FileSelectionToolbar
          selectedCount={selectedFileIds.size}
          isViewer={isViewer}
          onClearSelection={clearFileSelection}
          onMove={handleOpenMoveModal}
          onDelete={handleBatchDelete}
          onDownload={handleBatchDownload}
        />

        {/* Drag overlay - shown when dragging over entire files section */}
        <DragOverlay
          isDraggingOver={isDraggingOver}
          currentPath={currentPath}
          hasFiles={currentNodes.length > 0}
          isViewer={isViewer}
        />

        {/* Files List */}
        <div
          ref={filesContainerRef as React.RefObject<HTMLDivElement>}
          className="flex-1 min-h-0 overflow-y-auto pb-[calc(env(safe-area-inset-bottom)+1rem)] relative select-none"
          onMouseDown={handleFilesMouseDown}
        >
          {/* Selection box overlay - rendered via portal for proper positioning during scroll */}
          {isSelectingFiles &&
            createPortal(
              <div
                className="border-2 border-blue-500 bg-blue-500/10"
                style={filesSelectionBoxStyle}
              />,
              document.body
            )}
          {isLoadingFiles ? (
            <div className="flex items-center justify-center py-6 sm:py-8">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"></div>
            </div>
          ) : currentNodes.length === 0 ? (
            <EmptyFilesDropZone
              isDraggingOver={isDraggingOver}
              isMaxDepth={isMaxDepth}
              isUploadingToFolder={isUploadingToFolder}
              isViewer={isViewer}
              currentPath={currentPath}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onCreateFolder={onCreateFolder}
              onUploadClick={triggerFileUpload}
            />
          ) : (
            <div className="space-y-1 pr-1">
              {currentNodes.map((node, index) => (
                <FileListItem
                  key={node.id || index}
                  node={node}
                  index={index}
                  projectId={projectId}
                  isViewer={isViewer}
                  isFileSelected={selectedFileIds.has(node.id)}
                  isFileHovered={hoveredFileId === node.id}
                  selectedFileIds={selectedFileIds}
                  onFolderClick={handleFolderClick}
                  onFilePreview={openFilePreview}
                  onToggleFileSelect={toggleFileSelect}
                  onDownloadFile={handleDownloadFile}
                  onDeleteFile={handleDeleteFile}
                  onEditFolder={handleOpenEditFolder}
                  onMouseEnter={(n: FileNode) => {
                    setHoveredFileId(n.id);
                  }}
                  onMouseLeave={() => {
                    setHoveredFileId(null);
                  }}
                  registerRef={(id: string, el: HTMLDivElement | null) => {
                    if (el) {
                      fileItemRefs.current.set(id, el);
                    } else {
                      fileItemRefs.current.delete(id);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
